<template>
  <router-link class="w-full flex items-center px-3 h-22"
               :class="{ 'cursor-default': isItemSelected, 'text-gray-dark': !isItemSelected, 'cursor-loading': isLoading }"
               :to="{ name: routeProps.route, params: { [routeProps.itemIdName]: item.id, botId }, query: routeProps.query }"
               :id="item.id"
               replace
               @click.native.stop
               @mouseenter.native="showItemControls"
               @mouseleave.native="hideItemControls"
               @dragstart.native.prevent.stop
  >
    <div v-if="isAnswer && (!isNewItem || !isItemSelected)">
      <publish-toggle v-if="displayPublishToggle"
                      :answer="item"
                      :bot-id="botId"
                      :class="{ [`${toggleAnimationClass}`]: !isItemSelected }"
                      :tooltip-enabled="true"
                      :is-answer-loading="isLoading"
                      @click.native.prevent.stop
      />
      <answer-published-status v-else :published="!!item.published" :is-unknown-answer="isUnknownAnswer"/>
    </div>

    <div class="min-w-16 w-full flex gap-2 justify-between items-center relative"
         :class="nameWrapperClass"
    >
      <input-with-error-message v-if="isRenameMode"
                                id="list-section-name-input"
                                :value.sync="itemName"
                                :is-loading="isLoading"
                                :is-new-item="isNewItem"
                                @escape="$emit('set-rename-mode', { mode: false, id: item.id })"
                                @submit="handleSubmit"
                                class="w-full h-full justify-between"
      />

      <div v-else class="min-w-16 whitespace-nowrap font-medium truncate pr-10 flex items-center"
           :class="{ 'text-black': isItemSelected }"
      >
        {{ item.name }}<span v-if="isUnknownAnswer" class="text-xs">❓</span>
      </div>

      <side-list-menu v-if="!isRenameMode && (isItemSelected || displayMenu) && menuBuilder(item).length"
                      :items="menuBuilder(item)"
                      class="absolute right-0"
                      @show="handleDropdownItem('show', { id: item.id })"
                      @rename="handleDropdownItem('set-rename-mode', { mode: true, id: item.id })"
                      @toggle-publish-all-markets="handleDropdownItem('toggle-publish-all-markets', item)"
                      @delete="handleDropdownItem('request-delete', item)"
                      @copy-code="handleDropdownItem('copy-code',  item.id)"
                      @click.native.prevent.stop="displayMenu = true"
                      @enable-auto-translate="handleDropdownItem('enable-auto-translate', item)"
      />
    </div>
  </router-link>
</template>

<script>
import SideListMenu from '@/components/common/SideListMenu'
import AnswerPublishedStatus from '@/components/answers/AnswerPublishedStatus'
import PublishToggle from '@/components/answers/PublishToggle'
import listModule from '@/store/modules/list.module'
import editorUtils from '@/utils/editor.utils'
import InputWithErrorMessage from '@/components/common/InputWithErrorMessage.vue'
import WithLoader from '@/components/common/WithLoader.vue'

export default {
  name: 'ListSectionItem',

  components: {
    WithLoader,
    SideListMenu,
    AnswerPublishedStatus,
    PublishToggle,
    InputWithErrorMessage
  },

  props: {
    isItemSelected: {
      type: Boolean,
      default: false
    },
    routeProps: {
      type: Object,
      default: () => {}
    },
    item: {
      type: Object,
      default: () => {}
    },
    isAnswer: {
      type: Boolean,
      default: false
    },
    isNewItem: {
      type: Boolean,
      default: false
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    botId: {
      type: String,
      default: ''
    },
    isRenameMode: {
      type: Boolean,
      default: false
    },
    menuBuilder: {
      type: Function,
      default: () => {}
    }
  },

  data() {
    return {
      displayMenu: false,
      showToggle: false,
      toggleAnimationClass: '',
      isSubmitButtonHovered: false,
      itemName: ''
    }
  },

  computed: {
    hoveredItemId: listModule.getHoveredItemId,

    isUnknownAnswer() {
      if (!this.item.intent) return false

      return editorUtils.isUnknownAnswer(this.item.intent.platformId)
    },

    displayPublishToggle() {
      return (this.isItemSelected || this.showToggle) && !this.isUnknownAnswer
    },

    nameWrapperClass() {
      return {
        'pl-9': !this.displayPublishToggle && this.isAnswer,
        'ml-2': this.displayPublishToggle || !this.isAnswer,
        'text-black-soft': this.isItemSelected
      }
    }
  },

  watch: {
    // fallback when toggle gets stuck in display mode (caused by vx-tooltip
    // braking mouseleave event)
    hoveredItemId(hoveredItemId) {
      if (hoveredItemId === this.item.id || !this.showToggle) return

      this.hideItemControls()
    },

    item: {
      immediate: true,
      handler(item) {
        this.itemName = item.name
      }
    }
  },

  methods: {
    showItemControls() {
      listModule.setHoveredItemId(this.item.id)
      this.toggleAnimationClass = 'scale-in-left'
      this.displayMenu = true
      this.showToggle = true
    },

    async hideItemControls(e) {
      // prevents triggering mouse leave event, while mouse enters on tooltip
      if (e && e.toElement?.classList.contains('vs-tooltip')) return
      if (e && e.toElement?.classList.contains('vs-dropdown-menu')) return

      this.toggleAnimationClass = 'scale-out-left'
      this.displayMenu = false
      setTimeout(() => this.showToggle = false, 350)
    },

    handleDropdownItem(emit, payload) {
      this.$emit(emit, payload)
      this.hideItemControls()
    },

    handleSubmit(name) {
      this.$emit('rename', { ...this.item, name })
    }
  }
}
</script>

<style scoped lang="scss">
.scale-in-left {
  animation: scale-in-left 0.5s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
}

@keyframes scale-in-left {
  0% {
    transform: scale(0);
    transform-origin: 0% 50%;
    opacity: 1;
  }
  100% {
    transform: scale(1);
    transform-origin: 0% 50%;
    opacity: 1;
  }
}

.scale-out-left {
  animation: scale-out-left 0.5s cubic-bezier(0.550, 0.085, 0.680, 0.530) both;
}

@keyframes scale-out-left {
  0% {
    transform: scale(1);
    transform-origin: 0% 50%;
    opacity: 1;
  }
  100% {
    transform: scale(0);
    transform-origin: 0% 50%;
    opacity: 1;
  }
}
</style>
